import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import AppTextField from "../../lib/components/AppTextField";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Box, Button, InputAdornment, Paper, Skeleton } from "@mui/material";
import { FileDownload, Search } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";

import scrollHandler from "../../utils/scrollHandler";
import useDebounce from "../../hooks/useDebounce";
import organizationservice from "../../service/organizationservice";
import pathnames from "../../constants/pathnames";
import moment from "moment";
import ExportTransmissionModal from "./components/ExportTransmission";
import DateRangePreset, { buildDateParams, DATE_PRESETS, extractFilterDates, normalizeDatePreset } from "./components/DateRangePreset";
import SortFilterMenu from "./components/SortFilterMenu";
import DataTable from "../../lib/components/DataTable";

const SORT_BY_ITEMS = [
  { label: "Last Transmission", value: "lastTransmission" },
  { label: "Name", value: "name" },

];

const TransmissionPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFileTransmission = location.pathname === pathnames.TRANSMISSIONS;
  const isApiTransmission = location.pathname === pathnames.API_TRANSMISSIONS;

  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";
  const status = searchParams.get("status") || "";
  const urlDatePreset = searchParams.get("datePreset");
  const urlDateFrom = searchParams.get("dateFrom") || null;
  const urlDateTo = searchParams.get("dateTo") || null;
  const sortBy = searchParams.get("sortBy") || "lastTransmission";
  const sortOrder = searchParams.get("sortOrder") || "desc";

  const searchTerm = useDebounce(keyword);
  const [isExport, setIsExport] = React.useState(false);
  /** Synced from response.filters so first load shows backend default ("This month"). */
  const [responseFilters, setResponseFilters] = React.useState(null);

  const datePreset = normalizeDatePreset(urlDatePreset || responseFilters?.datePreset || DATE_PRESETS.THIS_MONTH);
  const dateFrom = urlDateFrom || responseFilters?.dateFrom || null;
  const dateTo = urlDateTo || responseFilters?.dateTo || null;

  const queries = {
    page,
    limit,
    keyword: searchTerm,
    status,
    sortBy,
    sortOrder,
    // First load (no URL preset): omit date params so backend defaults to THIS_MONTH.
    // After the user picks a preset (including THIS_MONTH), send it explicitly.
    ...(urlDatePreset ? buildDateParams(urlDatePreset, urlDateFrom, urlDateTo) : {}),
  };

  const organizationsQuery = useQuery({
    retry: false,
    queryKey: ["integrated-organizations", ...Object.values(queries)],
    queryFn: () => organizationservice.getFirsConnected({ ...queries }),
  });

  const { isPending: isLoading, data } = organizationsQuery;
  const organizationData = data?.data;
  const transmissionSummary = data?.data?.summary;

  React.useEffect(() => {
    const filters = organizationData?.filters;
    if (!filters) return;
    setResponseFilters(extractFilterDates(filters));
  }, [organizationData?.filters]);

  const gradientBackground = (from, to) => {
    return `linear-gradient(-5deg, ${from} 0%, ${to} 25%, transparent 25%,transparent 100%)`;
  };

  const stats = {
    totalOrganizationsConnected: transmissionSummary?.totalOrganizationsConnected ?? 0,
    totalTransmissions: transmissionSummary?.totalTransmissions ?? 0,
    successful: transmissionSummary?.successful ?? 0,
    failed: transmissionSummary?.failed ?? 0,
    pending: transmissionSummary?.pending ?? 0,
    totalInvoiceValue: formatTotalInvoiceValue(transmissionSummary?.totalInvoiceValue),
  };

  /* ---------------------------------------------------------------------- */
  /* DataTable Columns — use live top-level fields, not meta.extension.*     */
  /* ---------------------------------------------------------------------- */

  const columns = [
    {
      key: "name",
      header: "Client",
      render: (_, row) => (
        <Box display="flex" alignItems="center" gap={3}>
          <Link to={`${pathnames.TRANSMISSIONS}/${row.id}`} className="fw-medium">
            {row.name}
          </Link>
        </Box>
      ),
    },
    {
      key: "lastTransmissionAt",
      header: "Last Transmission Date",
      render: (_, row) => {
        const value = row?.lastTransmissionAt ?? row?.lastSyncedAt;
        return value ? moment(value).format("Do MMM, YYYY, h:mm A") : "N/A";
      },
    },
    {
      key: "joinedAt",
      header: "Joined Date",
      render: (_, row) => {
        const value = row?.joinedAt ?? row?.joinDate;
        return value ? moment(value).format("Do MMM, YYYY") : "N/A";
      },
    },
    {
      key: "totalUploads",
      header: "Uploads",
      render: (_, row) => row?.totalUploads ?? row?.totalFileUpload ?? row?.transmission?.totalFileUpload ?? 0,
    },
    {
      key: "totalTransmissions",
      header: "Transmissions",
      render: (_, row) => row?.totalTransmissions ?? row?.transmission?.total ?? 0,
    },
    {
      key: "totalSuccessful",
      header: "Successful",
      render: (_, row) => row?.totalSuccessful ?? row?.successful ?? row?.transmission?.successful ?? 0,
    },
    {
      key: "totalPending",
      header: "Processing",
      render: (_, row) => row?.totalPending ?? row?.pending ?? row?.transmission?.pending ?? 0,
    },
    {
      key: "totalFailed",
      header: "Failed",
      render: (_, row) => row?.totalFailed ?? row?.failed ?? row?.transmission?.failed ?? 0,
    },
  ];

  /* ---------------------------------------------------------------------- */
  /* Handlers                                                                */
  /* ---------------------------------------------------------------------- */

  function updateSearchParams(mutator) {
    const next = new URLSearchParams(window.location.search);
    mutator(next);
    navigate(
      {
        pathname: window.location.pathname,
        search: next.toString(),
      },
      { replace: true },
    );
  }

  function handleChange(e, name = "") {
    const value = name === "page" || name === "limit" ? e : e.target.value;

    updateSearchParams((params) => {
      if (value === "" || value == null) params.delete(name);
      else params.set(name, value);

      if (name !== "page") params.set("page", "1");
    });

    if (name === "page") scrollHandler();
  }

  function handlePresetChange(preset) {
    updateSearchParams((params) => {
      params.set("datePreset", preset);
      params.set("page", "1");

      if (preset !== DATE_PRESETS.CUSTOM) {
        params.delete("dateFrom");
        params.delete("dateTo");
      }
    });
  }

  function handleCustomRangeChange({ dateFrom: from, dateTo: to }) {
    updateSearchParams((params) => {
      params.set("datePreset", DATE_PRESETS.CUSTOM);
      params.set("dateFrom", from);
      params.set("dateTo", to);
      params.set("page", "1");
    });
  }

  /* ---------------------------------------------------------------------- */
  /* Render                                                                  */
  /* ---------------------------------------------------------------------- */

  return (
    <AppLayout>
      <Header title="FIRS Transmissions" description="Get insights to Firs Transmissions on here" />
      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <Box className="mb-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <Box
            sx={{
              display: "inline-flex",
              gap: "8px",
              padding: "6px",
              borderRadius: "14px",
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => navigate(pathnames.TRANSMISSIONS)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "10px",
                px: 3,
                color: isFileTransmission ? "#2563EB" : "#6B7280",
                backgroundColor: isFileTransmission ? "#EAF2FF" : "#FFFFFF",
                border: isFileTransmission ? "1.5px solid #2563EB" : "1.5px solid #D1D5DB",
                "&:hover": {
                  backgroundColor: "#EAF2FF",
                },
              }}
            >
              File Transmission
            </Button>

            <Button
              variant="outlined"
              onClick={() => navigate(pathnames.API_TRANSMISSIONS)}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderRadius: "10px",
                px: 3,
                color: isApiTransmission ? "#2563EB" : "#6B7280",
                backgroundColor: isApiTransmission ? "#EAF2FF" : "#FFFFFF",
                border: isApiTransmission ? "1.5px solid #2563EB" : "1.5px solid #D1D5DB",
                "&:hover": {
                  backgroundColor: "#EAF2FF",
                },
              }}
            >
              API
            </Button>
          </Box>

          <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
            <SortFilterMenu
              sortBy={sortBy}
              sortOrder={sortOrder}
              sortByItems={SORT_BY_ITEMS}
              onSortByChange={(e) => handleChange(e, "sortBy")}
              onSortOrderChange={(e) => handleChange(e, "sortOrder")}
            />
            <DateRangePreset
              datePreset={datePreset}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onPresetChange={handlePresetChange}
              onCustomRangeChange={handleCustomRangeChange}
            />
          </Box>
        </Box>

        <div className="row g-3 mb-3">
          {[
            { label: "Total Organizations", value: stats.totalOrganizationsConnected?.toLocaleString(), bg: gradientBackground("#a0f2e5", "#0cc6c3") },
            { label: "Total Transmissions", value: stats.totalTransmissions?.toLocaleString(), bg: gradientBackground("#56ccf2", "#2f80ed") },
            { label: "Successful Transmissions", value: stats.successful?.toLocaleString(), bg: gradientBackground("#fbc2eb", "#a18cd1") },
            { label: "Total Failed", value: stats.failed?.toLocaleString(), bg: gradientBackground("#8e2de2", "#4a00e0") },
            { label: "Pending", value: stats.pending?.toLocaleString(), bg: gradientBackground("#f2994a", "#f64f59") },
            {
              label: "Total Invoice Value",
              value: stats.totalInvoiceValue,
              solid: "#007bff",
            },
          ].map((card) => (
            <div className="col-12 col-lg-3" key={card.label}>
              <Paper
                className="p-3 h-100"
                style={
                  card.solid
                    ? { backgroundColor: card.solid, color: "#fff" }
                    : { backgroundColor: "#fff", backgroundImage: card.bg }
                }
              >
                <div className={`mb-3 ${card.solid ? "" : "text-secondary"}`}>{card.label}</div>
                {isLoading ? (
                  <Skeleton
                    variant="text"
                    width="60%"
                    sx={{ bgcolor: card.solid ? "rgba(255,255,255,0.35)" : undefined }}
                  />
                ) : (
                  <div className="fs-4 fw-medium text-break">{card.value}</div>
                )}
              </Paper>
            </div>
          ))}
        </div>

        <div className="d-flex justify-content-between align-items-center gap-4 w-100 mb-3">
          <div className="flex-grow-1" style={{ maxWidth: 520 }}>
            <AppTextField
              placeholder="Search transmissions..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <Button
            onClick={() => setIsExport(true)}
            endIcon={<FileDownload />}
            variant="outlined"
            color="secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            Run Report
          </Button>
        </div>

        <DataTable
          data={organizationData?.data || []}
          columns={columns}
          isLoading={isLoading}
          page={Math.max(1, parseInt(page, 10) || 1)}
          pageSize={Math.max(1, parseInt(limit, 10) || 20)}
          totalPages={organizationData?.totalPages || 1}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) => handleChange(value, "limit")}
          selectable={false}
          getRowId={(row) => row.id}
        />

      </div>

      <ExportTransmissionModal
        isOpen={isExport}
        onClose={() => setIsExport(false)}
        filters={{ keyword: searchTerm, status, sortBy, sortOrder, datePreset, dateFrom, dateTo }}
      />
    </AppLayout>
  );
};

export default TransmissionPage;

/**
 * summary.totalInvoiceValue may be a number, `{ total }`, or a currency map (`{ NGN: n }`).
 * Always prefer `total` — never use meta fields like `submitted`.
 */
function formatTotalInvoiceValue(value) {
  if (value == null || value === "") return "₦0";

  let amount = value;
  let currency = "₦";

  if (typeof value === "object") {
    if (value.total != null) {
      amount = value.total;
    } else if (value.NGN != null) {
      amount = value.NGN;
    } else {
      const entry = Object.entries(value).find(
        ([key]) => !["submitted", "pending", "failed", "successful", "count"].includes(key) && Number.isFinite(Number(value[key])),
      );
      amount = entry?.[1] ?? 0;
      if (entry?.[0] && entry[0] !== "total") currency = entry[0] === "NGN" ? "₦" : `${entry[0]} `;
    }
  }

  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return "₦0";

  return `${currency}${numeric.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}
